import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FolderIcon,
  TrashIcon,
  MapIcon,
  ArrowDownTrayIcon,
  MagnifyingGlassIcon,
  EllipsisVerticalIcon,
  CheckCircleIcon,
  ClockIcon,
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import { getDatasets, deleteDataset, exportDataset } from '../services/api';

function DatasetStatusBadge({ status, geocodedPercent }) {
  if (status === 'processing') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-blue-100 text-blue-700 rounded-full">
        <ClockIcon className="h-3 w-3 animate-spin" />
        Processing
      </span>
    );
  }

  if (geocodedPercent >= 90) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">
        <CheckCircleIcon className="h-3 w-3" />
        Ready
      </span>
    );
  }

  if (geocodedPercent > 0) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded-full">
        <ExclamationCircleIcon className="h-3 w-3" />
        Partial ({geocodedPercent}%)
      </span>
    );
  }

  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded-full">
      Not geocoded
    </span>
  );
}

function DatasetCard({ dataset, onDelete, onExport }) {
  const [showMenu, setShowMenu] = useState(false);

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 hover:border-gray-300 transition-colors">
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center">
            <div className="p-2 bg-blue-50 rounded-lg">
              <FolderIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-semibold text-gray-900">{dataset.name}</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Created {new Date(dataset.createdAt).toLocaleDateString()}
              </p>
            </div>
          </div>

          {/* Actions menu */}
          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
            >
              <EllipsisVerticalIcon className="h-5 w-5" />
            </button>

            {showMenu && (
              <>
                <div
                  className="fixed inset-0 z-10"
                  onClick={() => setShowMenu(false)}
                />
                <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
                  <Link
                    to={`/map?dataset=${dataset.id}`}
                    className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <MapIcon className="h-4 w-4" />
                    View on Map
                  </Link>
                  <button
                    onClick={() => {
                      onExport(dataset.id);
                      setShowMenu(false);
                    }}
                    className="flex items-center gap-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4" />
                    Export CSV
                  </button>
                  <hr className="my-1" />
                  <button
                    onClick={() => {
                      onDelete(dataset.id);
                      setShowMenu(false);
                    }}
                    className="flex items-center gap-2 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    <TrashIcon className="h-4 w-4" />
                    Delete
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Description */}
        {dataset.description && (
          <p className="mt-3 text-sm text-gray-600 line-clamp-2">
            {dataset.description}
          </p>
        )}

        {/* Stats */}
        <div className="mt-4 grid grid-cols-3 gap-4 text-center">
          <div>
            <div className="text-lg font-semibold text-gray-900">
              {dataset.voterCount?.toLocaleString() || 0}
            </div>
            <div className="text-xs text-gray-500">Voters</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-gray-900">
              {dataset.geocodedCount?.toLocaleString() || 0}
            </div>
            <div className="text-xs text-gray-500">Geocoded</div>
          </div>
          <div>
            <div className="text-lg font-semibold text-gray-900">
              {dataset.voterCount > 0
                ? `${Math.round((dataset.geocodedCount / dataset.voterCount) * 100)}%`
                : '0%'}
            </div>
            <div className="text-xs text-gray-500">Coverage</div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 rounded-b-lg flex items-center justify-between">
        <DatasetStatusBadge
          status={dataset.status}
          geocodedPercent={
            dataset.voterCount > 0
              ? Math.round((dataset.geocodedCount / dataset.voterCount) * 100)
              : 0
          }
        />
        <Link
          to={`/map?dataset=${dataset.id}`}
          className="text-sm text-blue-600 hover:text-blue-700 font-medium"
        >
          View Map
        </Link>
      </div>
    </div>
  );
}

export default function Datasets() {
  const queryClient = useQueryClient();
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch datasets
  const { data: datasetsData, isLoading } = useQuery({
    queryKey: ['datasets'],
    queryFn: getDatasets
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: deleteDataset,
    onSuccess: () => {
      queryClient.invalidateQueries(['datasets']);
    }
  });

  // Export handler
  const handleExport = async (datasetId) => {
    try {
      const blob = await exportDataset(datasetId, 'csv');
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dataset_${datasetId}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  // Delete handler
  const handleDelete = (datasetId) => {
    if (window.confirm('Are you sure you want to delete this dataset? This cannot be undone.')) {
      deleteMutation.mutate(datasetId);
    }
  };

  const datasets = datasetsData?.datasets || [];

  // Filter datasets by search
  const filteredDatasets = datasets.filter(ds =>
    ds.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (ds.description && ds.description.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Datasets</h1>
          <p className="mt-1 text-gray-600">
            Manage your imported voter data files
          </p>
        </div>
        <Link
          to="/import"
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
        >
          Import New Dataset
        </Link>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          type="text"
          placeholder="Search datasets..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {/* Dataset grid */}
      {isLoading ? (
        <div className="text-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto" />
          <p className="mt-4 text-gray-600">Loading datasets...</p>
        </div>
      ) : filteredDatasets.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <FolderIcon className="h-16 w-16 text-gray-300 mx-auto" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            {searchQuery ? 'No datasets found' : 'No datasets yet'}
          </h3>
          <p className="mt-2 text-gray-500">
            {searchQuery
              ? 'Try adjusting your search query'
              : 'Import your first voter dataset to get started'}
          </p>
          {!searchQuery && (
            <Link
              to="/import"
              className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Import Dataset
            </Link>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredDatasets.map(dataset => (
            <DatasetCard
              key={dataset.id}
              dataset={dataset}
              onDelete={handleDelete}
              onExport={handleExport}
            />
          ))}
        </div>
      )}

      {/* Delete error */}
      {deleteMutation.isError && (
        <div className="fixed bottom-4 right-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg shadow-lg">
          Failed to delete dataset: {deleteMutation.error?.message}
        </div>
      )}
    </div>
  );
}
